import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AddTimelineEventModal({ onClose, onSubmit, members }) {
  const [formData, setFormData] = useState({
    member_id: '',
    title: '',
    description: '',
    date: '',
    event_type: 'milestone'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.date) return;
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-event-modal">
      <div className="bg-white rounded-lg shadow-floating max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-paper border-b border-border p-6 flex justify-between items-center">
          <h2 className="text-2xl font-serif text-foreground">Add Timeline Event</h2>
          <button onClick={onClose} className="text-foreground-muted hover:text-primary" data-testid="close-modal-btn">
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <Label htmlFor="title" className="font-sans text-foreground mb-2 block">Event Title *</Label>
            <Input
              id="title"
              data-testid="event-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans"
              required
            />
          </div>

          <div>
            <Label htmlFor="date" className="font-sans text-foreground mb-2 block">Date *</Label>
            <Input
              id="date"
              type="date"
              data-testid="event-date-input"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans"
              required
            />
          </div>

          <div>
            <Label htmlFor="event_type" className="font-sans text-foreground mb-2 block">Event Type</Label>
            <Select value={formData.event_type} onValueChange={(value) => setFormData({ ...formData, event_type: value })}>
              <SelectTrigger data-testid="event-type-select" className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border border-border shadow-floating">
                <SelectItem value="milestone">Milestone</SelectItem>
                <SelectItem value="birth">Birth</SelectItem>
                <SelectItem value="death">Death</SelectItem>
                <SelectItem value="marriage">Marriage</SelectItem>
                <SelectItem value="graduation">Graduation</SelectItem>
                <SelectItem value="achievement">Achievement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="member_id" className="font-sans text-foreground mb-2 block">Related Family Member (Optional)</Label>
            <Select value={formData.member_id} onValueChange={(value) => setFormData({ ...formData, member_id: value })}>
              <SelectTrigger data-testid="member-select" className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-2 font-sans">
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-border shadow-floating">
                {members.map(member => (
                  <SelectItem key={member.member_id} value={member.member_id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="description" className="font-sans text-foreground mb-2 block">Description</Label>
            <Textarea
              id="description"
              data-testid="event-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-transparent border border-border focus:border-primary rounded-lg p-3 font-sans min-h-[100px]"
              placeholder="Tell us about this event..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-btn"
              className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif tracking-wide"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-event-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif tracking-wide"
            >
              Add Event
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}